/**
 * SeverityChip — SOC severity level primitive.
 *
 * Design rules (DESIGN.md):
 *   - ONLY --sev-* tokens. Never status-* (Low ≠ status-ok).
 *   - Loud-Fill Rule: Critical is the ONLY level with a solid saturated fill +
 *     sev-on ink. It is the one escalation signal that earns a visual interrupt.
 *   - Quiet pattern (High → Info): the LABEL itself is colored with the sev-* token —
 *     no dot, no background. Text color IS the redundant encoding (color + the word).
 *     This is the same pattern as DeviationCell.
 *   - Calm at rest: no uppercase, no bold, no tracking. Ambient emphasis on every row
 *     contradicts "loud only when it matters".
 *   - Column alignment: shared anatomy and px padding keep all levels flush in a table.
 */

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface SeverityChipProps {
  level: SeverityLevel;
}

const LEVEL_LABEL: Record<SeverityLevel, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

// Static class strings — dynamic interpolation is stripped by the Tailwind scanner.
const QUIET_COLOR: Record<Exclude<SeverityLevel, 'critical'>, string> = {
  high:   'text-sev-high-text font-medium',
  medium:    'text-sev-medium-text font-medium',
  low:      'text-sev-low-text',
  info: 'text-sev-info-text',
};

const BASE_CLASSES =
  'inline-flex items-center rounded px-1.5 py-0.5 text-xs leading-none';

export function SeverityChip({ level }: SeverityChipProps) {
  const label = LEVEL_LABEL[level];

  if (level === 'critical') {
    // Loud-Fill Rule: solid fill is the escalation signal reserved for Critical.
    return (
      <span className={`${BASE_CLASSES} uppercase bg-sev-critical text-sev-on`}>
        {label}
      </span>
    );
  }

  // Quiet: label text carries the severity color, same pattern as DeviationCell.
  return (
    <span className={`${BASE_CLASSES} ${QUIET_COLOR[level]}`}>
      {label}
    </span>
  );
}